import { useRef } from 'react';
import GraphPaperTool from './components/GraphPaperTool';
import SiteNav from './components/SiteNav';
import SiteFooter from './components/SiteFooter';
import ErrorBoundary from './components/ErrorBoundary';
import ErrorPage from './components/ErrorPage';
import usePageMeta from './hooks/usePageMeta';
import { useReveal } from './landing/pageShared';
import { LANDING_HOME_LINKS, LANDING_BY_PATH } from './landing/templates';
import './App.css';
import './animations.css';

const SPECIAL_PATHS = ['/about', '/contact', '/terms', '/privacy'];

function isKnownPath() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/' || Boolean(LANDING_BY_PATH[path]) || SPECIAL_PATHS.includes(path);
}

function App() {
  const toolRef = useRef(null);
  const revealRef = useReveal();
  const known = isKnownPath();

  usePageMeta(
    known
      ? {
          title: 'Printable Graph Paper - Free Printable Graph Paper PDF | Download & Print',
          description:
            'Create your own printable graph paper online for free. Download full page printable graph paper PDF in 1/4 inch, 1/2 inch, and 1 inch grids. Free printable graph paper with customizable grid types, colors, and page sizes. No signup required.',
          keywords:
            'printable graph paper, free printable graph paper, printable graph paper pdf, printable graph paper free, graph paper, grid paper, grid paper printable, graph online, grid paper pdf, graph paper printable, graph paper pdf',
          canonical: 'https://printablegraphpaperpdf.com',
          ogTitle: 'Printable Graph Paper - Free Printable Graph Paper PDF Generator',
          ogDescription:
            'Generate customizable printable graph paper online for free. Download printable graph paper PDF in 1/4 inch, 1/2 inch, and 1 inch grids. Square, dot, isometric, hexagonal, and polar grids available.',
          ogUrl: 'https://printablegraphpaperpdf.com',
          robots: 'index, follow',
        }
      : {
          title: 'Page Not Found (404) | Printable Graph Paper',
          description:
            'The page you are looking for could not be found. Create free printable graph paper online in 1/4 inch, 1/2 inch, 1 inch, A4, dot grid, isometric, and more.',
          canonical: null,
          ogTitle: 'Page Not Found (404) | Printable Graph Paper',
          ogDescription:
            'The page you are looking for could not be found. Create free printable graph paper online in 1/4 inch, 1/2 inch, 1 inch, A4, dot grid, isometric, and more.',
          robots: 'noindex, follow',
        }
  );

  if (!known) {
    return (
      <ErrorBoundary>
        <ErrorPage code={404} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>

    <div className="app" ref={revealRef}>
      <SiteNav
        onPrint={() => toolRef.current?.handlePrint()}
        onDownload={(f) => toolRef.current?.handleDownload(f)}
      />

      <h1 className="sr-only">
        Printable Graph Paper — Free Online Graph Paper Generator &amp; PDF Download
      </h1>

      <GraphPaperTool ref={toolRef} />

      <section className="home-papers">
        <div className="home-papers-inner">
          <div className="home-papers-head" data-reveal="true">
            <p className="home-papers-eyebrow">Explore the generators</p>
            <h2>Popular Graph Paper Types</h2>
            <p className="home-papers-lead">
              Each grid below has its own dedicated guide with specs, use cases, and a generator preconfigured to that exact paper.
            </p>
          </div>
          <div className="home-papers-grid" data-reveal="true" data-stagger="true">
            {LANDING_HOME_LINKS.map((l) => (
              <a className="home-paper-card" href={l.path} key={l.path}>
                <strong className="home-paper-title">{l.title}</strong>
                <span className="home-paper-blurb">{l.blurb}</span>
                <span className="home-paper-cta">Open generator →</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="seo-content">
        <div className="seo-inner" data-reveal="true">
          <h2>Free Printable Graph Paper — Create &amp; Download Custom Grids Online</h2>

          <p>Welcome to <strong>Printable Graph Paper</strong>, the ultimate online tool for creating custom <strong>printable graph paper</strong> in seconds. Whether you need <strong>free printable graph paper</strong> for math homework, engineering sketches, design layouts, or classroom activities, our generator lets you customize every detail and download high-quality <strong>printable graph paper PDF</strong> files instantly — no registration or signup required.</p>

          <h3>What Is Printable Graph Paper?</h3>
          <p>Graph paper, also known as grid paper, is paper printed with fine lines that form a regular grid. It is widely used in mathematics, engineering, architecture, art, and science for plotting data, drawing diagrams, sketching designs, and solving equations. <strong>Online printable graph paper</strong> tools allow you to generate custom grids tailored to your specific needs, rather than buying pre-printed pads that may not have the exact spacing or format you require.</p>

          <h3>Popular Graph Paper Types</h3>
          <p>Each graph paper style has its own dedicated guide with recommendations, specs, and a generator preconfigured to that exact grid. Browse the collection above, then generate your own customized grid in seconds.</p>

          <h3>Why Use Our Free Printable Graph Paper Generator?</h3>
          <p>Our <strong>free printable graph paper</strong> generator offers unmatched flexibility. You can choose from multiple grid types including square, dot, isometric, hexagonal, polar, and lined paper. Adjust the spacing from 2mm to 20mm, set custom line thickness, pick your preferred color, and select from popular page sizes like Letter, A4, Legal, A3, 11×17, and more. You can even switch between portrait and landscape orientations. The result is a <strong>downloadable full page printable graph paper PDF</strong> that prints perfectly every time.</p>

          <h3>Available Grid Types</h3>
          <p>We offer a wide variety of grid styles to suit every purpose:</p>
          <ul>
            <li><strong>Square Grid</strong> — The classic graph paper with evenly spaced horizontal and vertical lines. Perfect for plotting graphs, creating charts, and mathematical calculations. Choose from <strong>printable graph paper 1/4 inch</strong>, <strong>printable graph paper 1/2 inch</strong>, or <strong>1 inch printable graph paper</strong> grids.</li>
            <li><strong>Dot Grid</strong> — A clean grid of dots instead of lines. Ideal for bullet journaling, sketching, UI/UX wireframing, and architectural drawings where you need guidance without visual clutter.</li>
            <li><strong>Isometric Grid</strong> — Triangular grid patterns used for 3D drawings, engineering diagrams, and technical illustrations. Essential for creating perspective drawings and isometric designs.</li>
            <li><strong>Hexagonal Grid</strong> — Honeycomb-like pattern used in chemistry for organic molecules, in board game design, and in geographic mapping. A favorite among scientists and game developers.</li>
            <li><strong>Polar Grid</strong> — Concentric circles with radial lines, perfect for polar coordinate plotting, radar charts, and circular diagrams.</li>
            <li><strong>Lined Paper</strong> — Ruled paper with straight horizontal lines, suitable for note-taking, handwriting practice, and general writing tasks.</li>
          </ul>

          <h3>Customization Options</h3>
          <p>Our <strong>online printable graph paper</strong> tool puts you in full control. Adjust the grid spacing from fine 2mm grids for detailed work to large 20mm grids for elementary school activities. Change the line color from classic blue or gray to any color you prefer using the color picker. Set different thicknesses for minor and major grid lines, and choose an accent line every 5 or 10 squares for easier counting. With full control over page size and orientation, you can create the perfect <strong>printable graph paper free</strong> of charge.</p>

          <h3>Download &amp; Print Options</h3>
          <p>Once you have designed your perfect grid, download it as a <strong>downloadable printable graph paper PDF</strong> for high-quality printing. We also support PNG and SVG export formats for digital use. The <strong>free printable graph paper PDF</strong> files are optimized for printing at 96 DPI and work with any standard printer. Whether you need <strong>free printable graph paper 1/4 inch</strong> for math class or a <strong>full page free printable graph paper</strong> for engineering sketches, our tool delivers professional results in seconds. You can also print directly from your browser using Ctrl+P.</p>

          <h3>Who Uses Printable Graph Paper?</h3>
          <p><strong>Printable graph paper PDF</strong> downloads are popular among students, teachers, engineers, architects, designers, artists, and hobbyists. Teachers use it to prepare worksheets and exams. Students use it for math, physics, and chemistry assignments. Engineers and architects sketch floor plans, structural designs, and technical diagrams. Artists use dot grids and isometric paper for sketching and perspective drawing. Our tool serves all these use cases with a single, easy-to-use interface that works on any device — desktop, tablet, or phone.</p>

          <h3>Start Creating Your Printable Graph Paper Today</h3>
          <p>Stop searching for the perfect pre-printed grid. Our <strong>printable graph paper free</strong> online tool gives you everything you need to create, customize, and download the exact graph paper you need. With support for all major grid types, page sizes, and customization options, it is the most versatile <strong>free printable graph paper</strong> generator available. No signup, no Watermark, no limits. Start designing your <strong>printable graph paper</strong> now and download your <strong>printable graph paper PDF</strong> in seconds.</p>
        </div>
      </section>

      <section className="faq-section">
        <div className="faq-inner" data-reveal="true">
          <h2>Frequently Asked Questions About Printable Graph Paper</h2>

          <div className="faq-list">
            <details className="faq-item">
              <summary className="faq-question">What is printable graph paper?</summary>
              <div className="faq-answer">
                <p>Printable graph paper is paper that has been printed with fine lines forming a regular grid pattern. It is available as a digital PDF file that can be downloaded and printed at home or at the office. Unlike pre-printed graph paper notebooks, printable versions let you choose the exact grid type, spacing, line thickness, color, and page size you need.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">Is printable graph paper free to use?</summary>
              <div className="faq-answer">
                <p>Yes, our printable graph paper generator is completely free to use. There are no hidden fees, no subscriptions, and no registration required. You can create, customize, and download as many graph paper PDFs as you need without paying anything. The tool is free for personal, educational, and commercial use.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">Where can I download free printable graph paper?</summary>
              <div className="faq-answer">
                <p>You can download free printable graph paper directly from this website. Use the controls sidebar to select your preferred grid type (square, dot, isometric, hexagonal, polar, engineering, or lined), adjust the spacing and colors, choose your page size, and then click the Download button to save as PDF, PNG, or SVG. All downloads are free with no watermark.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">How do I print graph paper at home?</summary>
              <div className="faq-answer">
                <p>To print graph paper at home, simply customize your grid using the settings on the left, then click the Print button or press Ctrl+P (Cmd+P on Mac). Your browser will open the print dialog where you can select your printer, adjust settings like margins to none, and print. You can also download as a PDF first and print later.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">Can I print graph paper as a PDF?</summary>
              <div className="faq-answer">
                <p>Yes, you can print graph paper as a PDF in two ways. Click the Download button and select PDF to save a high-quality PDF file to your computer, then open and print it anytime. Alternatively, press Ctrl+P and choose "Save as PDF" in the print dialog. Both methods produce a crisp, print-ready PDF at the correct scale.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">How do I print graph paper in correct scale?</summary>
              <div className="faq-answer">
                <p>Our graph paper generator automatically outputs at 96 DPI, which means the grid lines will print at the exact physical size you selected (for example, 1/4 inch squares will measure exactly 1/4 inch on paper). For best results, make sure your printer is set to 100% scale (not "Fit to Page") in the print dialog and set margins to none or minimum.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">Can I print graph paper from Word?</summary>
              <div className="faq-answer">
                <p>While you can insert graph paper images or PDFs into Microsoft Word, we recommend using our dedicated online graph paper generator instead. It is specifically designed to produce correctly scaled, print-ready graph paper without the layout headaches of Word. Simply download the PDF from our tool and print directly.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">Can I print graph paper from Excel?</summary>
              <div className="faq-answer">
                <p>Excel can be used to create basic grids by formatting cells as squares, but it is not ideal for accurate graph paper. Our online generator is far more convenient and precise — it offers dedicated grid types like isometric, hexagonal, and polar that Excel cannot easily produce, and ensures correct scaling with no extra setup.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">What is graph paper used for?</summary>
              <div className="faq-answer">
                <p>Graph paper is used in mathematics for plotting graphs and solving geometry problems, in engineering for sketching designs and diagrams, in architecture for floor plans and blueprints, in science for recording data, in art for perspective drawing and pixel art, and in education for teaching math concepts. It is also popular for bullet journaling, cross-stitch patterns, and game design.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">What sizes are available in printable graph paper?</summary>
              <div className="faq-answer">
                <p>Our tool supports a wide range of page sizes including Letter (8.5×11″), A4 (210×297mm), Legal (8.5×14″), 11×17 (tabloid), A3, A2, Poster, and Movie Poster sizes. You can also switch between portrait and landscape orientation for any size, giving you full flexibility for your specific project needs.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">What is 1/4 inch graph paper used for?</summary>
              <div className="faq-answer">
                <p>1/4 inch graph paper (also called 4 squares per inch) is one of the most popular sizes. It is commonly used in elementary and middle school math classes for plotting graphs, practicing fractions, and learning coordinate systems. The 1/4 inch grid is also ideal for engineering sketches, architectural drafting, and detailed drawings where you need a balance between precision and readability.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">Can I customize printable graph paper before printing?</summary>
              <div className="faq-answer">
                <p>Absolutely. Our generator offers extensive customization options. You can change the grid type (square, dot, isometric, hexagonal, polar, engineering, lined), adjust spacing from 2mm to 20mm, select line color from 8 presets or a custom color picker, set minor and major line thickness, choose accent lines every 5 or 10 squares, pick page size and orientation, and even set margin position for lined paper.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">What is the difference between graph paper and grid paper?</summary>
              <div className="faq-answer">
                <p>Graph paper and grid paper are terms that are often used interchangeably. Generally, "graph paper" refers specifically to paper with a grid of fine lines used for mathematical graphing and plotting, while "grid paper" can be a broader term encompassing any paper with a regular grid pattern. In practice, they mean the same thing — a printable sheet with crossing horizontal and vertical lines forming squares.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">Can printable graph paper be used for math and engineering?</summary>
              <div className="faq-answer">
                <p>Yes, printable graph paper is widely used in both math and engineering. In mathematics, it is essential for graphing functions, geometry constructions, statistics plots, and calculus problems. In engineering, it is used for technical drawings, circuit diagrams, structural sketches, and CAD planning. Our tool includes specialized engineering graph paper with major lines every 5 squares for easier counting.</p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-question">How do I print graph paper without margins or borders?</summary>
              <div className="faq-answer">
                <p>To print graph paper without margins or borders, open your browser's print dialog (Ctrl+P) and set margins to "None" or "Minimum." Under scaling options, choose "Actual Size" (100%) rather than "Fit to Page." If your printer supports borderless printing, enable that option. For the most consistent results, download as a PDF first and use a PDF viewer's borderless print settings.</p>
              </div>
            </details>
          </div>
        </div>

        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is printable graph paper?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Printable graph paper is paper that has been printed with fine lines forming a regular grid pattern. It is available as a digital PDF file that can be downloaded and printed at home or at the office. Unlike pre-printed graph paper notebooks, printable versions let you choose the exact grid type, spacing, line thickness, color, and page size you need."
                }
              },
              {
                "@type": "Question",
                "name": "Is printable graph paper free to use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, our printable graph paper generator is completely free to use. There are no hidden fees, no subscriptions, and no registration required. You can create, customize, and download as many graph paper PDFs as you need without paying anything. The tool is free for personal, educational, and commercial use."
                }
              },
              {
                "@type": "Question",
                "name": "Where can I download free printable graph paper?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can download free printable graph paper directly from this website. Use the controls sidebar to select your preferred grid type (square, dot, isometric, hexagonal, polar, engineering, or lined), adjust the spacing and colors, choose your page size, and then click the Download button to save as PDF, PNG, or SVG. All downloads are free with no watermark."
                }
              },
              {
                "@type": "Question",
                "name": "How do I print graph paper at home?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "To print graph paper at home, simply customize your grid using the settings on the left, then click the Print button or press Ctrl+P (Cmd+P on Mac). Your browser will open the print dialog where you can select your printer, adjust settings like margins to none, and print. You can also download as a PDF first and print later."
                }
              },
              {
                "@type": "Question",
                "name": "Can I print graph paper as a PDF?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, you can print graph paper as a PDF in two ways. Click the Download button and select PDF to save a high-quality PDF file to your computer, then open and print it anytime. Alternatively, press Ctrl+P and choose \"Save as PDF\" in the print dialog. Both methods produce a crisp, print-ready PDF at the correct scale."
                }
              },
              {
                "@type": "Question",
                "name": "How do I print graph paper in correct scale?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our graph paper generator automatically outputs at 96 DPI, which means the grid lines will print at the exact physical size you selected. For best results, make sure your printer is set to 100% scale (not \"Fit to Page\") in the print dialog and set margins to none or minimum."
                }
              },
              {
                "@type": "Question",
                "name": "Can I print graph paper from Word?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "While you can insert graph paper images or PDFs into Microsoft Word, we recommend using our dedicated online graph paper generator instead. It is specifically designed to produce correctly scaled, print-ready graph paper without the layout headaches of Word. Simply download the PDF from our tool and print directly."
                }
              },
              {
                "@type": "Question",
                "name": "Can I print graph paper from Excel?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Excel can be used to create basic grids by formatting cells as squares, but it is not ideal for accurate graph paper. Our online generator is far more convenient and precise — it offers dedicated grid types like isometric, hexagonal, and polar that Excel cannot easily produce, and ensures correct scaling with no extra setup."
                }
              },
              {
                "@type": "Question",
                "name": "What is graph paper used for?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Graph paper is used in mathematics for plotting graphs and solving geometry problems, in engineering for sketching designs and diagrams, in architecture for floor plans and blueprints, in science for recording data, in art for perspective drawing and pixel art, and in education for teaching math concepts. It is also popular for bullet journaling, cross-stitch patterns, and game design."
                }
              },
              {
                "@type": "Question",
                "name": "What sizes are available in printable graph paper?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our tool supports a wide range of page sizes including Letter (8.5x11\"), A4 (210x297mm), Legal (8.5x14\"), 11x17 (tabloid), A3, A2, Poster, and Movie Poster sizes. You can also switch between portrait and landscape orientation for any size, giving you full flexibility for your specific project needs."
                }
              },
              {
                "@type": "Question",
                "name": "What is 1/4 inch graph paper used for?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "1/4 inch graph paper (also called 4 squares per inch) is one of the most popular sizes. It is commonly used in elementary and middle school math classes for plotting graphs, practicing fractions, and learning coordinate systems. The 1/4 inch grid is also ideal for engineering sketches, architectural drafting, and detailed drawings where you need a balance between precision and readability."
                }
              },
              {
                "@type": "Question",
                "name": "Can I customize printable graph paper before printing?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Our generator offers extensive customization options. You can change the grid type (square, dot, isometric, hexagonal, polar, engineering, lined), adjust spacing from 2mm to 20mm, select line color from 8 presets or a custom color picker, set minor and major line thickness, choose accent lines every 5 or 10 squares, pick page size and orientation, and even set margin position for lined paper."
                }
              },
              {
                "@type": "Question",
                "name": "What is the difference between graph paper and grid paper?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Graph paper and grid paper are terms that are often used interchangeably. Generally, \"graph paper\" refers specifically to paper with a grid of fine lines used for mathematical graphing and plotting, while \"grid paper\" can be a broader term encompassing any paper with a regular grid pattern. In practice, they mean the same thing — a printable sheet with crossing horizontal and vertical lines forming squares."
                }
              },
              {
                "@type": "Question",
                "name": "Can printable graph paper be used for math and engineering?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, printable graph paper is widely used in both math and engineering. In mathematics, it is essential for graphing functions, geometry constructions, statistics plots, and calculus problems. In engineering, it is used for technical drawings, circuit diagrams, structural sketches, and CAD planning. Our tool includes specialized engineering graph paper with major lines every 5 squares for easier counting."
                }
              },
              {
                "@type": "Question",
                "name": "How do I print graph paper without margins or borders?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "To print graph paper without margins or borders, open your browser's print dialog (Ctrl+P) and set margins to \"None\" or \"Minimum.\" Under scaling options, choose \"Actual Size\" (100%) rather than \"Fit to Page.\" If your printer supports borderless printing, enable that option. For the most consistent results, download as a PDF first and use a PDF viewer's borderless print settings."
                }
              }
            ]
          })
        }} />
      </section>

      <SiteFooter />
    </div>
    </ErrorBoundary>
  );
}

export default App;